import { usesHostedBilling, usesStoreBilling } from '@/lib/appDistribution';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { StyleSheet, View, Pressable, Platform, Alert } from 'react-native';
import useTheme from '@/hooks/useNeutralTheme';
import { isDarkTheme } from '@/constants/Colors';
import { useCreditPacksStore, useModalStore, useAuthStore } from '@/store';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { getCreditPackIapProductId } from '@/hooks/useIosIap';
import { nativeCreditPackPrice } from '@/lib/nativePayment';
import AppStoreCatalogStatus from './AppStoreCatalogStatus';
import useResponsiveness from '@/hooks/useResponsiveness';
import CreditsFAQs from '@/components/Credits/CreditsFAQs';
import PayWithBitcoinRow from '@/components/Credits/PayWithBitcoinRow';
import PremiumUpsellRow from '@/components/Credits/PremiumUpsellRow';
import AutoTopUpOfferRow from '@/components/Credits/AutoTopUpOfferRow';
import BannerPlacement from '@/components/Banner/BannerPlacement';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { PLUS_MONTHLY_CREDITS_DISPLAY } from '@/constants/config';
import { formatCreditBalance, wholeCredits } from '@/lib/formatCredits';
import { trackEvent } from '@/constants/utils/analytics';


function CreditsPurchaseModalUrgent({
  isPlus,
  isPlusPremium,
  isIapSubscriber,
  onClose,
  totalAvailableCredits,
  onLowerActionsChange,
  promoCode,
}) {
  const { colors, theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(colors, isDark, isXS), [colors, isDark, isXS]);
  const { openModal } = useModalStore(state => state.actions);
  const { openCreditPacksCheckout, usesWhopCheckout, iap } = useGabPurchase();

  const creditPacks = useCreditPacksStore(state => state.creditPacks);
  const fetchCreditPacks = useCreditPacksStore(state => state.fetchCreditPacks);

  const hasPaymentMethod = useAuthStore(state => state.user?.valmar?.hasPaymentMethod);
  const maskedCardData = useAuthStore(state => state.user?.valmar?.maskedCardData);
  const canQuickPurchase = usesHostedBilling() && !usesWhopCheckout && hasPaymentMethod && maskedCardData?.maskedNumber;

  const [selectedPackId, setSelectedPackId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    Promise.resolve(fetchCreditPacks()).catch(() => {});
  }, [fetchCreditPacks]);

  const displayedPacks = useMemo(() => {
    if (!creditPacks?.length) return [];
    return [...creditPacks]
      .filter(p => p.flags?.isDisplayed !== false)
      .sort((a, b) => (a.orderWeight ?? 0) - (b.orderWeight ?? 0));
  }, [creditPacks]);

  // Auto-select cheapest pack (lowest barrier to continue)
  useEffect(() => {
    if (selectedPackId || displayedPacks.length === 0) return;
    setSelectedPackId(displayedPacks[0]?.id);
  }, [displayedPacks, selectedPackId]);

  const selectedPack = useMemo(
    () => displayedPacks.find(p => p.id === selectedPackId),
    [displayedPacks, selectedPackId],
  );

  const isZeroCredits = totalAvailableCredits <= 0;

  const getPackPrice = useCallback((pack) => {
    if (!pack) return '';
    if (usesStoreBilling()) {
      const iapProductId = getCreditPackIapProductId(pack.credits);
      const iapProduct = iapProductId ? iap?.getProduct?.(iapProductId) : null;
      return nativeCreditPackPrice(iapProduct) || 'Store price unavailable';
    }
    return `$${(pack.price ?? 0).toFixed(2)}`;
  }, [iap]);

  const purchaseBlocked = isLoading || (usesStoreBilling() && (iap?.purchasePending || iap?.purchaseInProgress
    || !nativeCreditPackPrice(iap?.getProduct?.(getCreditPackIapProductId(selectedPack?.credits)))));

  const handleBuy = useCallback(async () => {
    if (!selectedPack || purchaseBlocked) return;
    trackEvent('conversion', 'credit_pack_selected', String(selectedPack.credits), selectedPack.price);
    setIsLoading(true);
    try {
      if (canQuickPurchase) {
        openModal('QuickPurchaseConfirmModal', { creditPackId: selectedPack.id });
      } else {
        await openCreditPacksCheckout({ creditPackId: selectedPack.id, credits: selectedPack.credits, promoCode });
      }
    } catch (error) {
      console.error('Urgent variant purchase error:', error);
      if (Platform.OS !== 'web') {
        Alert.alert('Purchase Error', error?.message || 'Failed to purchase credit pack.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedPack, canQuickPurchase, openModal, openCreditPacksCheckout, promoCode, purchaseBlocked]);

  useEffect(() => {
    if (!onLowerActionsChange) return;
    const priceLabel = selectedPack ? getPackPrice(selectedPack) : '';
    const creditsLabel = selectedPack ? (selectedPack.credits ?? 0).toLocaleString() : '';

    const title = selectedPack
      ? (isXS ? `Continue — ${priceLabel}` : `Continue — ${creditsLabel} credits for ${priceLabel}`)
      : 'Select a credit pack';

    onLowerActionsChange([
      {
        title,
        icon: 'coins',
        variant: 'purple',
        onPress: handleBuy,
        disabled: !selectedPack || purchaseBlocked,
        isLoading,
      },
    ]);
  }, [selectedPack, isLoading, isXS, handleBuy, getPackPrice, onLowerActionsChange, purchaseBlocked]);

  const handleUpgrade = useCallback(() => {
    onClose?.();
    openModal('PricingModal');
  }, [onClose, openModal]);

  return (
    <View style={styles.container}>
      <AppStoreCatalogStatus iap={iap} credits />
      <BannerPlacement placement="credit-store" />

      {/* Contextual status */}
      <View style={styles.statusBar}>
        <View style={styles.statusPill}>
          <Icon name="coins" size={12} color={isZeroCredits ? (isDark ? '#FBBF24' : '#92400E') : colors.text_color_secondary} />
          <Text size="xs" weight="semibold" style={{ color: isZeroCredits ? (isDark ? '#FBBF24' : '#92400E') : colors.text_color_secondary }}>
            {isZeroCredits ? '0 credits remaining' : `${formatCreditBalance(totalAvailableCredits)} credit${wholeCredits(totalAvailableCredits) !== 1 ? 's' : ''} remaining`}
          </Text>
        </View>
        <View style={styles.pausedIndicator}>
          <View style={styles.amberDot} />
          <Text size="xs" weight="medium" style={{ color: (isDark ? '#FBBF24' : '#92400E') }}>Reply paused</Text>
        </View>
      </View>

      {/* Header */}
      <View style={styles.header}>
        <Text size="xl" weight="bold" color="primary">
          {isZeroCredits ? 'Top up to continue' : 'Running low — top up now'}
        </Text>
        <Text size="s" color="secondary" style={styles.subtitle}>
          {isZeroCredits
            ? 'Your conversation is paused. Pick a pack to resume instantly — credits never expire.'
            : "Don't lose your flow. Top up now and keep going — credits never expire."}
        </Text>
      </View>

      {/* Pack selection chips */}
      <View style={styles.packsSection}>
        <Text size="xs" weight="semibold" color="tertiary">
          Choose a credit pack
        </Text>
        <View style={styles.packsGrid}>
          {displayedPacks.map((pack) => {
            const isSelected = selectedPackId === pack.id;
            return (
              <Pressable
                key={pack.id}
                onPress={() => setSelectedPackId(pack.id)}
                disabled={isLoading}
                style={({ hovered }) => [
                  styles.packChip,
                  isSelected && styles.packChipSelected,
                  hovered && !isSelected && !isLoading && styles.packChipHovered,
                  isLoading && !isSelected && styles.packChipDisabled,
                ]}
              >
                {(pack.flags?.isPopular || pack.flags?.isBestValue) && (
                  <View style={[styles.chipTag, pack.flags?.isBestValue ? styles.chipTagBest : styles.chipTagPopular]}>
                    <Text size="xs" weight="bold" style={styles.chipTagText}>
                      {pack.flags?.isBestValue ? 'Best value' : 'Popular'}
                    </Text>
                  </View>
                )}
                {isSelected && (
                  <View style={styles.chipCheck}>
                    <Icon name="check" size={8} color={colors.accent_text_color} />
                  </View>
                )}
                <View style={styles.chipCreditsRow}>
                  <Text size="l" weight="bold" color="primary">
                    {(pack.credits ?? 0).toLocaleString()}
                  </Text>
                  <Text size="xs" color="secondary">credits</Text>
                </View>
              </Pressable>
            );
          })}
        </View>
      </View>

      <PayWithBitcoinRow
        creditPackId={selectedPack?.id}
        price={selectedPack?.price}
        disabled={isLoading}
      />

      {/* Plus upsell */}
      {!isPlus && (
        <Pressable
          style={({ hovered }) => [styles.plusRow, hovered && styles.plusRowHovered]}
          onPress={handleUpgrade}
        >
          <Icon name="sparkles" size={14} color={colors.text_color_primary} solid />
          <View style={styles.plusTextContainer}>
            <Text size="s" weight="semibold" color="primary">
              Never run out again
            </Text>
            <Text size="xs" color="secondary">
              Plus — free messages + {PLUS_MONTHLY_CREDITS_DISPLAY} credits/mo
            </Text>
          </View>
          <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
        </Pressable>
      )}

      {/* Plus Premium is web-only; never upsell on top of App Store / Play Plus. */}
      {isPlus && !isPlusPremium && !isIapSubscriber && usesHostedBilling() && (
        <PremiumUpsellRow onClose={onClose} />
      )}

      <AutoTopUpOfferRow />

      <CreditsFAQs />
    </View>
  );
}

const createStyles = (colors, isDark, isXS) => StyleSheet.create({
  container: {
    gap: 16,
    padding: isXS ? 16 : 24,
  },
  statusBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  statusPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.08)',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: isDark ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.15)',
  },
  pausedIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  amberDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: (isDark ? '#FBBF24' : '#92400E'),
  },
  header: {
    gap: 4,
  },
  subtitle: {
    lineHeight: 20,
  },
  packsSection: {
    gap: 8,
  },
  packsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  packChip: {
    position: 'relative',
    flex: 1,
    minWidth: isXS ? '45%' : 110,
    backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 12,
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border_color_primary,
    gap: 2,
    overflow: 'hidden',
    cursor: 'pointer',
    ...Platform.select({
      web: {
        transitionProperty: 'border-color, background-color, box-shadow',
        transitionDuration: '120ms',
      },
    }),
  },
  packChipSelected: {
    borderColor: colors.text_color_primary,
    backgroundColor: colors.solid_color_tertiary,
    ...Platform.select({
      web: {
        boxShadow: `0 0 0 1px ${colors.text_color_primary}40`,
      },
    }),
  },
  packChipHovered: {
    borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)',
    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
  },
  packChipDisabled: {
    opacity: 0.5,
  },
  chipTag: {
    position: 'absolute',
    top: 0,
    right: 0,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderBottomLeftRadius: 6,
  },
  chipTagBest: {
    backgroundColor: colors.text_color_primary,
  },
  chipTagPopular: {
    backgroundColor: colors.text_color_primary,
  },
  chipTagText: {
    color: colors.accent_text_color,
    fontSize: 11,
  },
  chipCreditsRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
  },
  chipCheck: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 18,
    height: 18,
    borderBottomRightRadius: 8,
    backgroundColor: colors.text_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  plusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 0,
  },
  plusRowHovered: {
    backgroundColor: colors.solid_color_block,
  },
  plusTextContainer: {
    flex: 1,
  },
});

export default React.memo(CreditsPurchaseModalUrgent);
